import { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Input,
  Button,
  Stack,
  Select,
  HStack,
  VStack,
  Portal,
  IconButton,
  Container,
  createListCollection,
} from "@chakra-ui/react";
import { useExpenseLimits } from "../context/ExpenseLimitContext";
import { useCurrentMonth } from "../hooks/useCurrentMonth";
import { formatEuro, formatMonthYear } from "../utils/helperFunctions";
import { IoCloseSharp } from "react-icons/io5";
import { Toaster, toaster } from "../components/ui/toaster";
import { useCustomCategories } from "../context/CustomCatgoriesContext";

type SelectTypes = {
  label: string;
  value: string;
};

export const ExpenseLimits = () => {
  const currentMonth = useCurrentMonth();
  const { expenseLimits, updateExpenseLimit, removeExpenseLimit } =
    useExpenseLimits();
  const { categories: customCategories } = useCustomCategories();
  const [selectedCategory, setSelectedCategory] = useState("");
  const [amount, setAmount] = useState("");
  const monthBudgets = expenseLimits[currentMonth] || [];
  const validCategoryValues = customCategories.map((c) => c.value);

  const filteredBudgets = monthBudgets.filter((b) =>
    validCategoryValues.includes(b.category)
  );

  const categoryCollection = createListCollection({
    items: customCategories.map((cat) => ({
      label: cat.label,
      value: cat.value,
    })),
  });

  const handleSave = () => {
    if (!selectedCategory || !amount) return;

    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount)) return;

    updateExpenseLimit(currentMonth, selectedCategory, parsedAmount);

    toaster.create({
      description: "Das Limit wurde erfolgreich gesetzt",
      type: "success",
      placement: "top-end",
    });

    // Reset inputs
    setSelectedCategory("");
    setAmount("");
  };

  const handleRemoveExpenseLimit = (category: string) => {
    removeExpenseLimit(currentMonth, category);
  };

  return (
    <Container maxW="lg" py={10} pb={{ base: 24, md: 10 }}>
      <Heading textAlign="center">
        {" "}
        Ausgabenlimits für {formatMonthYear(currentMonth)}
      </Heading>
      <Toaster />
      <Box
        maxW="lg"
        mx="auto"
        mt={10}
        p={6}
        borderWidth="1px"
        borderRadius="md"
        boxShadow="md"
      >
        <Heading size="lg" mb={2} textAlign="center">
          Ausgabenlimits anlegen
        </Heading>
        <Text mb={6} textAlign="justify">
          Lege hier deine wunsch Budget-Grenzen für verschiedene Kategorien
          fest.
        </Text>
        <Stack spaceY={4}>
          <Select.Root
            width={"100%"}
            size="sm"
            collection={categoryCollection}
            onChange={(e: any) => setSelectedCategory(e.target.value)}
            required
          >
            <Select.HiddenSelect />
            <Select.Control>
              <Select.Trigger borderColor="gray">
                <Select.ValueText placeholder="Kategorie wählen" />
              </Select.Trigger>
              <Select.IndicatorGroup>
                <Select.Indicator />
              </Select.IndicatorGroup>
            </Select.Control>
            <Portal>
              <Select.Positioner>
                <Select.Content>
                  {categoryCollection.items.map((item: SelectTypes) => (
                    <Select.Item item={item} key={item.value}>
                      {item.label}
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>
          <Input
            type="number"
            placeholder="€ Betrag"
            value={amount}
            borderColor="gray"
            onChange={(e) => setAmount(e.target.value)}
          />
          <Button onClick={handleSave}>Speichern</Button>
        </Stack>

        {/* Zeige gespeicherte Budgets */}
        {filteredBudgets.length > 0 && (
          <Box mt={8}>
            <Heading size="md" mb={4}>
              Angelegte Limits
            </Heading>
            <Stack spaceY={3}>
              {filteredBudgets.map((budget) => (
                <VStack align="stretch" style={{ margin: 0, paddingBottom: 0 }}>
                  <HStack
                    key={budget.category}
                    justify="space-between"
                    p={3}
                    gap={"2rem"}
                    bg="gray.100"
                    borderRadius="md"
                    overflow={"auto"}
                  >
                    <Box>
                      <Text
                        maxWidth={150}
                        overflowWrap={"break-word"}
                        fontWeight="medium"
                      >
                        {budget.category}
                      </Text>
                    </Box>
                    <Text fontWeight="bold" color={"red"}>
                      {formatEuro(budget.amount)}
                    </Text>

                    <IconButton
                      size="sm"
                      aria-label="Löschen"
                      onClick={() => handleRemoveExpenseLimit(budget.category)}
                      colorScheme="red"
                      variant="ghost"
                      position={"sticky"}
                      right="-13px"
                      bg="gray.100"
                      zIndex={10}
                    >
                      {/* @ts-ignore */}
                      <IoCloseSharp />
                    </IconButton>
                  </HStack>
                </VStack>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Container>
  );
};
